import { SimCard, WarningCircle, WifiHigh } from "@phosphor-icons/react";
import { useCallback, useMemo, useState } from "react";
import PhoneInput from "../../../components/SearchablePhoneInput";
import { HashLoader } from "react-spinners";
import { toast } from "sonner";
import { useAuth } from "../../../context/AuthContext";
import { formatAsMoney } from "../../../helpers/numbers";
import { getFriendlyBillsErrorMessage } from "../../../helpers/billsErrors";
import useBillDataPlans, {
  BillDataPlan,
} from "../../../hooks/useBillDataPlans";
import useBillDataProviders from "../../../hooks/useBillDataProviders";
import { BillNetwork } from "../../../hooks/useBillAirtimeProviders";
import useBillDataPurchase, {
  BillDataPurchasePayload,
} from "../../../hooks/useBillDataPurchase";
import BillPurchaseSuccess from "./BillPurchaseSuccess";
import Confirm from "../Confirm";
import BillPageHeader from "./components/BillPageHeader";
import SelectField from "./components/SelectField";
import ProviderSelectModal from "./components/ProviderSelectModal";
import PlanSelectModal, {
  BillPlanOption,
} from "./components/PlanSelectModal";
import BillSummaryCard from "./components/BillSummaryCard";
import SetPinPrompt from "./components/SetPinPrompt";

const WALLET_PAYMENT_OPTION_CODE = "004";

const toNumber = (value: unknown) => {
  if (typeof value === "number") {
    return value;
  }

  if (typeof value === "string") {
    return Number(value.replace(/[^\d.-]/g, "")) || 0;
  }

  return 0;
};

const formatPlanSize = (plan: BillDataPlan) =>
  `${plan.name}${plan.volume ? String(plan.volume).toUpperCase() : ""}`;

const generateAgentReference = () =>
  `txn_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;

const normalizePlanType = (planType: string) =>
  planType
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const DataBundlesTemp = () => {
  const { user, refetch } = useAuth();

  const [selectedProvider, setSelectedProvider] = useState<BillNetwork | null>(
    null
  );
  const [selectedPlan, setSelectedPlan] = useState<BillDataPlan | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string>();
  const [confirming, setConfirming] = useState(false);
  const [pin, setPin] = useState("");
  const [payload, setPayload] = useState<BillDataPurchasePayload | null>(null);

  const [providerModalOpen, setProviderModalOpen] = useState(false);
  const [planModalOpen, setPlanModalOpen] = useState(false);

  const { providers, fetchingProviders, isProvidersError, providersError } =
    useBillDataProviders();

  const { plans, fetchingPlans, isPlansError, plansError } = useBillDataPlans(
    selectedProvider?.code || ""
  );

  const {
    purchaseData,
    purchasingData,
    isPurchaseError,
    purchaseError,
    purchaseResponse,
  } = useBillDataPurchase({
    onSuccess(data) {
      toast.success(data?.message || "Data bundle purchased successfully");
      refetch();
      setConfirming(false);
    },
    onError(error) {
      toast.error(getFriendlyBillsErrorMessage(error, "data"));
    },
  });

  const planOptions = useMemo<BillPlanOption[]>(
    () =>
      [...plans]
        .sort((a, b) => Number(a.amount) - Number(b.amount))
        .map((plan) => ({
          id: plan.code,
          name: formatPlanSize(plan),
          subtitle: plan.validity,
          badge: plan.type ? normalizePlanType(plan.type) : undefined,
          amount: Number(plan.amount),
          validity: plan.validity,
        })),
    [plans]
  );

  const availableBalance = toNumber(user?.wallet?.available_balance);
  const hasEnoughBalance =
    !!selectedPlan && availableBalance >= Number(selectedPlan.amount);
  const canProceed =
    !!selectedProvider &&
    !!selectedPlan &&
    !!phoneNumber &&
    hasEnoughBalance &&
    !purchasingData;
  const purchaseErrorMessage = getFriendlyBillsErrorMessage(
    purchaseError,
    "data"
  );

  const handleProviderSelect = (provider: BillNetwork) => {
    setSelectedProvider(provider);
    setSelectedPlan(null);
  };

  const handlePlanSelect = (id: string) => {
    const plan = plans.find((p) => p.code === id) || null;
    setSelectedPlan(plan);
  };

  const handleSubmit = () => {
    if (!user?.pin_status) {
      return;
    }

    if (!selectedProvider || !selectedPlan || !phoneNumber) {
      toast.error("Select a provider, plan, and phone number to continue.");
      return;
    }

    if (!hasEnoughBalance) {
      toast.error("Insufficient wallet balance.");
      return;
    }

    setPayload({
      pin: "",
      phone: phoneNumber,
      service_type: selectedProvider.code,
      plan_code: selectedPlan.code,
      amount: Number(selectedPlan.amount),
      payment_option_code: WALLET_PAYMENT_OPTION_CODE,
      agentReference: generateAgentReference(),
    });
    setConfirming(true);
  };

  const authorizePurchase = useCallback(() => {
    if (!payload || !pin) {
      return;
    }

    purchaseData({
      ...payload,
      pin,
    });
  }, [payload, pin, purchaseData]);

  if (!user?.pin_status) {
    return <SetPinPrompt redirect="/payments/bills/data" action="buying data" />;
  }

  return (
    <div className="relative min-h-[70vh] px-5 py-8">
      {(fetchingProviders || purchasingData) && (
        <div className="fixed top-0 z-[120] left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <HashLoader />
        </div>
      )}

      {purchaseResponse && (
        <BillPurchaseSuccess
          title="Data purchase successful!"
          message={
            <>
              Congratulations! You have successfully purchased{" "}
              <span className="font-bold">
                {selectedPlan ? formatPlanSize(selectedPlan) : "data"}
              </span>{" "}
              for <span className="font-bold">{phoneNumber}</span>.
            </>
          }
        />
      )}

      {confirming && selectedProvider && selectedPlan && (
        <Confirm
          title="Confirm Data Purchase"
          setConfirming={setConfirming}
          onAuthorized={authorizePurchase}
          onPinChange={setPin}
          description={
            <div>
              You are about to buy{" "}
              <span className="font-bold">{formatPlanSize(selectedPlan)}</span>{" "}
              {selectedProvider.name} data for{" "}
              <span className="font-bold">{phoneNumber}</span> at{" "}
              <span className="font-bold">
                NGN {formatAsMoney(selectedPlan.amount)}
              </span>
              .
            </div>
          }
        />
      )}

      <div className="mx-auto max-w-5xl flex flex-col gap-6">
        <BillPageHeader
          title="Buy Data"
          subtitle="Pick a network and plan, pay from your wallet."
          icon={WifiHigh}
          walletBalance={availableBalance}
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
          <section className="lg:col-span-2 bg-white rounded-2xl border border-gray-200 p-5 shadow-sm flex flex-col gap-5">
            <div>
              <label className="text-sm font-medium text-gray-700">
                Recipient phone number
              </label>
              <PhoneInput
                placeholder="Enter phone number"
                international
                defaultCountry="NG"
                withCountryCallingCode
                value={phoneNumber}
                onChange={setPhoneNumber}
                className="mt-2 focus:outline-none flex-1 flex items-center rounded-xl py-2 border border-gray-300 shadow-sm px-4 w-full focus-within:outline-deepBlue focus-within:outline"
              />
            </div>

            <SelectField
              label="Network"
              placeholder="Select network"
              icon={SimCard}
              value={selectedProvider?.name}
              onClick={() => setProviderModalOpen(true)}
              loading={fetchingProviders}
              loadingText="Loading networks..."
            />

            <SelectField
              label="Data plan"
              placeholder={
                selectedProvider
                  ? "Select a data plan"
                  : "Select a network first"
              }
              value={
                selectedPlan
                  ? `${formatPlanSize(selectedPlan)} — NGN ${formatAsMoney(
                      selectedPlan.amount
                    )}`
                  : undefined
              }
              onClick={() => setPlanModalOpen(true)}
              disabled={!selectedProvider}
              loading={!!selectedProvider && fetchingPlans}
              loadingText="Loading plans..."
            />

            {isProvidersError && (
              <div className="rounded-xl bg-red-50 text-red-600 p-4 text-sm">
                {providersError?.message || "Unable to load providers."}
              </div>
            )}

            {isPlansError && (
              <div className="rounded-xl bg-red-50 text-red-600 p-4 text-sm">
                {plansError?.message || "Unable to load data plans."}
              </div>
            )}

            {!!selectedProvider && !fetchingPlans && plans.length === 0 && (
              <div className="rounded-xl bg-gray-50 border border-dashed border-gray-300 p-4 text-center text-gray-500 text-sm">
                No plans are available for this provider right now.
              </div>
            )}
          </section>

          <BillSummaryCard
            rows={[
              { label: "Network", value: selectedProvider?.name || "Not selected" },
              { label: "Phone", value: phoneNumber || "Not entered" },
              {
                label: "Plan",
                value: selectedPlan ? formatPlanSize(selectedPlan) : "Not selected",
              },
              {
                label: "Validity",
                value: selectedPlan?.validity || "Not selected",
              },
            ]}
            amount={selectedPlan ? Number(selectedPlan.amount) : 0}
            canProceed={canProceed}
            onProceed={handleSubmit}
            alerts={
              <>
                {!!selectedPlan && !hasEnoughBalance && (
                  <div className="rounded-xl bg-red-50 text-red-600 p-4 text-sm flex gap-2">
                    <WarningCircle size={20} weight="bold" />
                    <span>
                      Your wallet balance is not enough for this plan. Please
                      fund your wallet and try again.
                    </span>
                  </div>
                )}
                {isPurchaseError && (
                  <div className="rounded-xl bg-red-50 text-red-600 p-4 text-sm">
                    {purchaseErrorMessage}
                  </div>
                )}
              </>
            }
          />
        </div>
      </div>

      <ProviderSelectModal
        open={providerModalOpen}
        onClose={() => setProviderModalOpen(false)}
        providers={providers}
        selectedCode={selectedProvider?.code}
        onSelect={handleProviderSelect}
        label="network"
        icon={SimCard}
      />

      <PlanSelectModal
        open={planModalOpen}
        onClose={() => setPlanModalOpen(false)}
        plans={planOptions}
        selectedId={selectedPlan?.code}
        onSelect={handlePlanSelect}
        loading={fetchingPlans}
      />
    </div>
  );
};

export default DataBundlesTemp;
